import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { useHistory } from "react-router";

import { InvestmentStyleDiagnosisRequest } from "@/api/generated";
import { useFromHomeEntry } from "@/hooks/useFromHomeEntry";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { extractHttpStatus } from "@/utilities/extractHttpStatus";
import { clearSavedAnswers, markCompleted } from "@/utilities/investmentStyleDiagnosisStorage";
import { NotifyNetworkError } from "@/utilities/NotifyNetworkError";

import { DiagnosisSubmitFailureMessage } from "../components/DiagnosisSubmitFailureMessage";
import { DiagnosisSubmitSessionExpiredMessage } from "../components/DiagnosisSubmitSessionExpiredMessage";
import { buildSubmitStep } from "../utils/submitStep";

import { shouldShowSupportLink } from "./investmentStyleDiagnosisSubmitRetry";

interface UseSubmitInvestmentStyleDiagnosis {
  submit: (request: InvestmentStyleDiagnosisRequest) => Promise<void>;
  submitting: boolean;
}

export const useSubmitInvestmentStyleDiagnosis = (): UseSubmitInvestmentStyleDiagnosis => {
  const history = useHistory();
  const fromHome = useFromHomeEntry();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  // レンダーに影響しない純粋なカウンタなので useState ではなく useRef で持つ。
  const consecutiveFailureCountRef = useRef(0);

  const submit = async (request: InvestmentStyleDiagnosisRequest) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const fp = PropallyApiFactory();
      await fp.v1InvestmentStyleDiagnosisPost(request, { withCredentials: true });
      if (!fromHome) {
        sendEventToAppMeasurementTool(AppEventTokenEnum.InvestmentStyleDiagnosisCompleted);
      }
      // 送信済みの回答は不要になるため破棄し、完了フラグを立てる。
      // 遷移先画面からブラウザバックしてもフォームの履歴エントリへ戻らないよう push ではなく replace で遷移する。
      clearSavedAnswers();
      markCompleted();
      // 直前の失敗で報告フォーム/セッション切れの永続バナーが出ていた場合、次の画面まで残らないよう閉じる。
      closeSnackbar("network_error");
      history.replace(buildSubmitStep(fromHome).path, { fromHome });
    } catch (error) {
      // 既に診断済み(409)なら結果画面へ（新規完了ではないため完了イベントは送らない）
      const status = extractHttpStatus(error);
      if (status === 409) {
        // 新規完了ではないが「既に完了している」ことに変わりはないため、成功時と対称に扱う。
        clearSavedAnswers();
        markCompleted();
        closeSnackbar("network_error");
        history.replace("/investment_style_diagnosis/result", { fromHome });
        return;
      }
      // 全問回答した直後の失敗は離脱に直結するため、成功と対称に計測する
      if (!fromHome) {
        sendEventToAppMeasurementTool(AppEventTokenEnum.InvestmentStyleDiagnosisSubmitFailed);
      }
      if (status === 401) {
        // セッション切れは「時間を置いて再試行」しても成功しないため、1回目から永続的な案内に倒す
        // （通常の自動で消えるスナックバーだと、何度失敗しても救済導線に辿り着けないままになるため）。
        // 直前に別内容の永続バナー（report/session_expired）が出ていた場合に備え、閉じてから出し直す
        // （同じ key "network_error" での再 enqueue は notistack 側で黙って無視されうるため）。
        closeSnackbar("network_error");
        NotifyNetworkError(enqueueSnackbar, closeSnackbar, DiagnosisSubmitSessionExpiredMessage);
        setSubmitting(false);
        return;
      }
      consecutiveFailureCountRef.current += 1;
      if (shouldShowSupportLink(consecutiveFailureCountRef.current)) {
        // 強制ゲート（WithAuth.tsx）により診断未完了ユーザーはこの画面から動けないため、
        // 通信障害等で送信が繰り返し失敗する状況で手詰まり感だけを与えないための最終手段。
        closeSnackbar("network_error");
        NotifyNetworkError(enqueueSnackbar, closeSnackbar, DiagnosisSubmitFailureMessage);
      } else {
        enqueueSnackbar("診断結果の送信に失敗しました。時間を置いて再度お試しください。", { variant: "error" });
      }
      setSubmitting(false);
    }
  };

  return { submit, submitting };
};
